import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The People service's public contract.
 *
 * Other services depend on *this file only*. They never import People's domain
 * entities, and they never query the `people` schema — a Group holds a
 * `personId` and resolves it through this contract or through a domain event.
 */

/**
 * `DELETED` is distinct from `ARCHIVED`: it means someone asked to delete
 * this person and the system could not do so safely (they have history in
 * another service), so it recorded that intent instead of silently treating
 * it as an archive. Not restorable to `ACTIVE` for the same reason — see
 * `Person.delete()`.
 */
export const PERSON_STATUSES = ['ACTIVE', 'INACTIVE', 'DECEASED', 'ARCHIVED', 'DELETED'] as const;
export type PersonStatus = (typeof PERSON_STATUSES)[number];

export const GENDERS = ['MALE', 'FEMALE', 'UNSPECIFIED'] as const;
export type Gender = (typeof GENDERS)[number];

export const MARITAL_STATUSES = [
  'UNKNOWN',
  'SINGLE',
  'MARRIED',
  'DIVORCED',
  'WIDOWED',
  'SEPARATED',
] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

/** `RNC` (Registro Nacional del Contribuyente) alongside personal-identity
 *  documents — some records carry it for a person acting as an
 *  organization's legal representative. Both this and `documentNumber` are
 *  independently optional; a document number with no chosen type (or vice
 *  versa) is a valid partial state, not an error. */
export const DOCUMENT_TYPES = ['CEDULA', 'PASSPORT', 'DRIVERS_LICENSE', 'RNC'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const CONTACT_TYPES = ['EMAIL', 'MOBILE', 'HOME_PHONE', 'WORK_PHONE', 'OTHER'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

/** The three phone-ish contact types the repeatable "Sección de Contacto"
 *  list offers — Casa/Trabajo/Celular. `EMAIL`/`OTHER` stay reachable
 *  through the same `/people/:id/contacts` endpoints but aren't offered
 *  by that picker; the three named emails on Person cover email instead. */
export const PERSON_PHONE_CONTACT_TYPES = ['HOME_PHONE', 'WORK_PHONE', 'MOBILE'] as const;

export const PERSON_ACADEMIC_LEVELS = [
  'PRIMARY',
  'SECONDARY',
  'TECHNICAL',
  'UNDERGRADUATE',
  'POSTGRADUATE',
] as const;
export type PersonAcademicLevel = (typeof PERSON_ACADEMIC_LEVELS)[number];

export const PERSON_ACADEMIC_LEVEL_STATUSES = ['IN_PROGRESS', 'COMPLETED', 'INCOMPLETE'] as const;
export type PersonAcademicLevelStatus = (typeof PERSON_ACADEMIC_LEVEL_STATUSES)[number];

export const PERSON_BLOOD_TYPES = [
  'A_POSITIVE',
  'A_NEGATIVE',
  'B_POSITIVE',
  'B_NEGATIVE',
  'AB_POSITIVE',
  'AB_NEGATIVE',
  'O_POSITIVE',
  'O_NEGATIVE',
] as const;
export type PersonBloodType = (typeof PERSON_BLOOD_TYPES)[number];

/** Kebab-case for the URL (`/people/catalogs/:kind`); mapped to the Prisma
 *  `PersonCatalogKind` enum server-side. */
export const PERSON_CATALOG_KINDS = ['profession', 'occupation', 'economic-sector'] as const;
export type PersonCatalogKind = (typeof PERSON_CATALOG_KINDS)[number];

export const MEMBERSHIP_STATUSES = [
  'PENDING',
  'ACTIVE',
  'INACTIVE',
  'ENDED',
  'TRANSFERRED',
] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const HOUSEHOLD_ROLES = ['HEAD', 'SPOUSE', 'CHILD', 'DEPENDENT', 'OTHER'] as const;
export type HouseholdRole = (typeof HOUSEHOLD_ROLES)[number];

export const RELATIONSHIP_TYPES = [
  'SPOUSE',
  'PARENT',
  'CHILD',
  'SIBLING',
  'GUARDIAN',
  'GRANDPARENT',
  'GRANDCHILD',
  'AUNT_UNCLE',
  'FRIEND',
  'CAREGIVER',
  'OTHER',
] as const;
export type RelationshipType = (typeof RELATIONSHIP_TYPES)[number];

// --- Requests ---------------------------------------------------------------

/**
 * A name is required; everything else is optional.
 *
 * Church data arrives incomplete — a visitor card may carry only a first name.
 * Forcing more fields would push staff toward placeholder values, which is
 * worse for data quality than an honest null.
 */
export const nameField = z.string().trim().min(1).max(100);

export const personContactInputSchema = z.object({
  type: z.enum(CONTACT_TYPES),
  value: z.string().trim().min(1).max(320),
  isPrimary: z.boolean().default(false),
});

/**
 * A single address per person — matching what the directory actually shows
 * (one "Dirección" block), not a multi-address book. PATCH semantics on the
 * parent request: omitting `address` leaves it unchanged, `address: null`
 * clears it, an object replaces it wholesale (addresses are edited as a
 * unit — a partial line1-only patch would leave city/region stale from a
 * previous, different address).
 */
export const personAddressInputSchema = z.object({
  line1: z.string().trim().max(200).nullish(),
  line2: z.string().trim().max(200).nullish(),
  city: z.string().trim().max(120).nullish(),
  region: z.string().trim().max(120).nullish(),
  postalCode: z.string().trim().max(20).nullish(),
  /** ISO 3166-1 alpha-2. */
  countryCode: z.string().trim().length(2).nullish(),
});
export type PersonAddressInput = z.infer<typeof personAddressInputSchema>;

export const createPersonRequestSchema = z.object({
  firstName: nameField,
  lastName: nameField,
  middleName: z.string().trim().max(100).nullish(),
  preferredName: z.string().trim().max(100).nullish(),
  birthDate: z.iso.date().nullish(),
  gender: z.enum(GENDERS).default('UNSPECIFIED'),
  maritalStatus: z.enum(MARITAL_STATUSES).default('UNKNOWN'),
  status: z.enum(PERSON_STATUSES).default('ACTIVE'),
  documentType: z.enum(DOCUMENT_TYPES).nullish(),
  documentNumber: z.string().trim().max(50).nullish(),
  campusId: z.uuid().nullish(),
  organizationId: z.uuid().nullish(),
  contacts: z.array(personContactInputSchema).max(10).default([]),
  tagIds: z.array(z.uuid()).max(50).default([]),
  notes: z.string().trim().max(4000).nullish(),
});
export type CreatePersonRequest = z.infer<typeof createPersonRequestSchema>;

/**
 * PATCH semantics: an omitted key means "leave unchanged", an explicit `null`
 * means "clear this value".
 *
 * Declared explicitly rather than derived with `.partial()` from the create
 * schema. `.partial()` makes fields optional but does NOT strip their
 * `.default()`, so `PATCH {}` would parse into
 * `{ gender: 'UNSPECIFIED', maritalStatus: 'UNKNOWN', status: 'ACTIVE' }` and
 * silently overwrite three real values with defaults — a data-corruption bug
 * that looks like a successful no-op request.
 */
export const updatePersonRequestSchema = z
  .object({
    firstName: nameField.optional(),
    lastName: nameField.optional(),
    middleName: z.string().trim().max(100).nullish(),
    preferredName: z.string().trim().max(100).nullish(),
    birthDate: z.iso.date().nullish(),
    gender: z.enum(GENDERS).optional(),
    maritalStatus: z.enum(MARITAL_STATUSES).optional(),
    status: z.enum(PERSON_STATUSES).optional(),
    documentType: z.enum(DOCUMENT_TYPES).nullish(),
    documentNumber: z.string().trim().max(50).nullish(),
    campusId: z.uuid().nullish(),
    organizationId: z.uuid().nullish(),
    communicationsOptOut: z.boolean().optional(),
    /** The `FileObject` id from a completed upload — null clears the photo. */
    photoFileId: z.uuid().nullish(),
    address: personAddressInputSchema.nullish(),
    /** ISO 3166-1 alpha-2. */
    birthCountryCode: z.string().trim().length(2).nullish(),
    personalEmail: z.email().trim().max(320).nullish(),
    workEmail: z.email().trim().max(320).nullish(),
    secondaryEmail: z.email().trim().max(320).nullish(),
    academicLevel: z.enum(PERSON_ACADEMIC_LEVELS).nullish(),
    academicLevelStatus: z.enum(PERSON_ACADEMIC_LEVEL_STATUSES).nullish(),
    professionId: z.uuid().nullish(),
    occupationId: z.uuid().nullish(),
    economicSectorId: z.uuid().nullish(),
    bloodType: z.enum(PERSON_BLOOD_TYPES).nullish(),
    medicalIndications: z.string().trim().max(4000).nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdatePersonRequest = z.infer<typeof updatePersonRequestSchema>;

export const deletePersonRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type DeletePersonRequest = z.infer<typeof deletePersonRequestSchema>;

// --- Person contacts (post-creation edit path) -------------------------------

/** Only the three phone types the "Sección de Contacto" list offers — see
 *  `PERSON_PHONE_CONTACT_TYPES`. */
export const createPersonContactRequestSchema = z.object({
  type: z.enum(PERSON_PHONE_CONTACT_TYPES),
  value: z.string().trim().min(1).max(320),
  isPrimary: z.boolean().default(false),
});
export type CreatePersonContactRequest = z.infer<typeof createPersonContactRequestSchema>;

export const updatePersonContactRequestSchema = z
  .object({
    value: z.string().trim().min(1).max(320).optional(),
    isPrimary: z.boolean().optional(),
  })
  .refine((value) => value.value !== undefined || value.isPrimary !== undefined, {
    message: 'At least one field must be supplied',
  });
export type UpdatePersonContactRequest = z.infer<typeof updatePersonContactRequestSchema>;

// --- Person catalogs (Profesión / Ocupación / Sector Económico) -------------

export const createPersonCatalogEntryRequestSchema = z.object({
  name: z.string().trim().min(1).max(150),
});
export type CreatePersonCatalogEntryRequest = z.infer<typeof createPersonCatalogEntryRequestSchema>;

export const listPersonCatalogEntriesQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListPersonCatalogEntriesQuery = z.infer<typeof listPersonCatalogEntriesQuerySchema>;

export interface PersonCatalogEntryResponse {
  readonly id: string;
  readonly kind: PersonCatalogKind;
  readonly name: string;
  readonly archivedAt: string | null;
}

// --- Households ---------------------------------------------------------------

export const createHouseholdRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  campusId: z.uuid().nullish(),
});
export type CreateHouseholdRequest = z.infer<typeof createHouseholdRequestSchema>;

export const updateHouseholdRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    campusId: z.uuid().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateHouseholdRequest = z.infer<typeof updateHouseholdRequestSchema>;

export const archiveHouseholdRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchiveHouseholdRequest = z.infer<typeof archiveHouseholdRequestSchema>;

export const addHouseholdMemberRequestSchema = z.object({
  personId: z.uuid(),
  role: z.enum(HOUSEHOLD_ROLES).default('OTHER'),
  isPrimaryContact: z.boolean().default(false),
});
export type AddHouseholdMemberRequest = z.infer<typeof addHouseholdMemberRequestSchema>;

// --- Relationships --------------------------------------------------------------

/**
 * `personId` is the URL param (`POST /people/:id/relationships`), not part
 * of the body — a relationship is always created from one person's side.
 */
export const createRelationshipRequestSchema = z.object({
  relatedPersonId: z.uuid(),
  relationshipType: z.enum(RELATIONSHIP_TYPES),
  /** e.g. `relationshipType: GUARDIAN` with `inverseType: CHILD` records
   *  "Ana is Bruno's guardian" and "Bruno is Ana's child" as one fact. */
  inverseType: z.enum(RELATIONSHIP_TYPES).nullish(),
});
export type CreateRelationshipRequest = z.infer<typeof createRelationshipRequestSchema>;

// --- Bulk import ----------------------------------------------------------------

export const IMPORT_ROW_STATUSES = ['created', 'updated', 'skipped', 'error'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

/**
 * One CSV row, already parsed by the web client — `papaparse` runs in the
 * browser, not the server, so this is structured JSON rather than a raw
 * file upload. No multipart handling exists anywhere in this platform, and
 * client-side parsing gives an operator immediate feedback on a malformed
 * file before it ever reaches the network.
 */
export const importPeopleRowInputSchema = z.object({
  firstName: nameField,
  lastName: nameField,
  /** Empty/omitted is fine — a visitor card may carry no email at all —
   *  but when present it's the dedup key `PeopleService.importPeople`
   *  matches against via `findByNormalizedContact`. */
  email: z.email().trim().toLowerCase().nullish(),
  phone: z.string().trim().max(50).nullish(),
});
export type ImportPeopleRowInput = z.infer<typeof importPeopleRowInputSchema>;

export const importPeopleRequestSchema = z.object({
  rows: z.array(importPeopleRowInputSchema).min(1).max(2000),
});
export type ImportPeopleRequest = z.infer<typeof importPeopleRequestSchema>;

/** Redeemed by the public unsubscribe link a broadcast email carries — see
 *  `signUnsubscribeToken` in `@tabita/auth`. No session; the token alone
 *  names the tenant and the person. */
export const unsubscribeRequestSchema = z.object({
  token: z.string().min(1),
});
export type UnsubscribeRequest = z.infer<typeof unsubscribeRequestSchema>;

export const archivePersonRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type ArchivePersonRequest = z.infer<typeof archivePersonRequestSchema>;

export const listPeopleQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Matches against name and primary contact. Always tenant-scoped. */
  search: z.string().trim().max(200).nullish(),
  status: z.enum(PERSON_STATUSES).nullish(),
  campusId: z.uuid().nullish(),
  organizationId: z.uuid().nullish(),
  householdId: z.uuid().nullish(),
  tagId: z.uuid().nullish(),
  gender: z.enum(GENDERS).nullish(),
  maritalStatus: z.enum(MARITAL_STATUSES).nullish(),
  birthDateFrom: z.iso.date().nullish(),
  birthDateTo: z.iso.date().nullish(),
  /** Archived records are excluded unless explicitly requested (rule RR-012). */
  includeArchived: queryBoolean().default(false),
  /** Independent of `includeArchived` — `ARCHIVED` and `DELETED` are two
   *  distinct terminal statuses (see `Person.archive()`/`Person.delete()`),
   *  each shown only when its own preference is on. */
  includeDeleted: queryBoolean().default(false),
  sort: z.enum(['name', '-name', 'createdAt', '-createdAt']).default('name'),
});
export type ListPeopleQuery = z.infer<typeof listPeopleQuerySchema>;

/** For the dashboard's birthday widget — `withinDays` is a horizon from
 *  today, not a calendar range, so it stays correct across a year boundary
 *  (e.g. Dec 29 within 7 days reaches Jan 5). */
export const listUpcomingBirthdaysQuerySchema = z.object({
  withinDays: z.coerce.number().int().min(1).max(365).default(30),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListUpcomingBirthdaysQuery = z.infer<typeof listUpcomingBirthdaysQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface PersonContactResponse {
  readonly id: string;
  readonly type: ContactType;
  readonly value: string;
  readonly isPrimary: boolean;
  readonly isVerified: boolean;
}

export interface AddressResponse {
  readonly line1: string | null;
  readonly line2: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postalCode: string | null;
  readonly countryCode: string | null;
}

export interface HouseholdMemberResponse {
  readonly personId: string;
  readonly displayName: string;
  readonly role: HouseholdRole;
  readonly isPrimaryContact: boolean;
}

export interface HouseholdResponse {
  readonly id: string;
  readonly name: string;
  readonly campusId: string | null;
  readonly memberCount: number;
  readonly archivedAt: string | null;
}

export interface HouseholdDetailResponse extends HouseholdResponse {
  readonly members: readonly HouseholdMemberResponse[];
}

export interface PersonRelationshipResponse {
  readonly id: string;
  readonly relatedPersonId: string;
  readonly relatedPersonDisplayName: string;
  readonly relatedPersonPhotoFileId: string | null;
  readonly relationshipType: RelationshipType;
  readonly inverseType: RelationshipType | null;
}

export interface PersonSummaryResponse {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName: string | null;
  readonly displayName: string;
  readonly status: PersonStatus;
  readonly campusId: string | null;
  readonly primaryEmail: string | null;
  readonly primaryPhone: string | null;
  /** A `FileObject` id — the web client resolves it to a signed URL via `GET /files/:id/download-url`. */
  readonly photoFileId: string | null;
  readonly archivedAt: string | null;
}

export interface PersonDetailResponse extends PersonSummaryResponse {
  readonly middleName: string | null;
  readonly birthDate: string | null;
  readonly gender: Gender;
  readonly maritalStatus: MaritalStatus;
  readonly documentType: DocumentType | null;
  readonly documentNumber: string | null;
  readonly organizationId: string | null;
  /** Kept alongside `household` for the existing `householdId` list filter — derived as `household?.id ?? null`. */
  readonly householdId: string | null;
  readonly household: HouseholdDetailResponse | null;
  readonly relationships: readonly PersonRelationshipResponse[];
  readonly address: AddressResponse | null;
  readonly contacts: readonly PersonContactResponse[];
  readonly tagIds: readonly string[];
  readonly communicationsOptOut: boolean;
  /** ISO 3166-1 alpha-2. */
  readonly birthCountryCode: string | null;
  readonly personalEmail: string | null;
  readonly workEmail: string | null;
  readonly secondaryEmail: string | null;
  readonly academicLevel: PersonAcademicLevel | null;
  readonly academicLevelStatus: PersonAcademicLevelStatus | null;
  /** Resolved from the tenant's own picklist — same posture as
   *  `TenantResponse.denomination`/`.council`. */
  readonly profession: PersonCatalogEntryResponse | null;
  readonly occupation: PersonCatalogEntryResponse | null;
  readonly economicSector: PersonCatalogEntryResponse | null;
  readonly bloodType: PersonBloodType | null;
  readonly medicalIndications: string | null;
  readonly deletedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PersonBirthdayResponse {
  readonly id: string;
  readonly displayName: string;
  readonly photoFileId: string | null;
  /** The person's actual birth date, ISO `YYYY-MM-DD` — year included, so
   *  `turningAge` is exact rather than inferred. */
  readonly birthDate: string;
  /** This year's — or, once that's passed, next year's — occurrence, ISO
   *  `YYYY-MM-DD`. */
  readonly nextBirthdayDate: string;
  /** 0 means today. */
  readonly daysUntil: number;
  readonly turningAge: number;
}

export interface DeletePersonResponse {
  /** `DELETED`: the row is gone, `person` is null. `MARKED_DELETED`: the
   *  person had history elsewhere, so nothing was removed — `status` became
   *  `DELETED` instead, and the full record is returned. */
  readonly outcome: 'DELETED' | 'MARKED_DELETED';
  readonly person: PersonDetailResponse | null;
}

export interface ImportPeopleRowResult {
  /** 1-based, matching the CSV's own row numbering (header excluded) — what
   *  an operator sees when they open the file in a spreadsheet. */
  readonly row: number;
  readonly status: ImportRowStatus;
  readonly personId: string | null;
  readonly message: string | null;
}

export interface ImportPeopleResponse {
  readonly results: readonly ImportPeopleRowResult[];
  readonly created: number;
  readonly updated: number;
  readonly skipped: number;
  readonly errors: number;
}

/**
 * The projection other services receive when they resolve a person reference.
 *
 * Deliberately minimal: a Groups module needs a name to render a roster, not a
 * birth date or a household. Widening this is a contract change, reviewed as one.
 */
export interface PersonReference {
  readonly id: string;
  readonly displayName: string;
  readonly status: PersonStatus;
}

/**
 * What the Communications module needs to actually reach someone: an
 * address per channel (either may be null — not everyone has a phone on
 * file) and whether they have opted out of broadcast messages. Separate
 * from `PersonReference` rather than widening it, since no other consumer
 * has a legitimate reason to read a contact address off a person reference.
 */
/** A tag as a picker option — deliberately not the full `Tag` record (no
 *  color, no audit fields). Tag management itself (create/rename/archive)
 *  has no endpoint yet; this exists so a caller can list what already
 *  exists to filter or tag by, e.g. Communications' audience picker. */
export interface TagSummaryResponse {
  readonly id: string;
  readonly name: string;
  readonly category: string | null;
}

export interface AudienceMemberReference {
  readonly personId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly communicationsOptOut: boolean;
}

/** Every active, non-archived person; those tagged with at least one of
 *  `tagIds`; or an explicit, caller-chosen set of people (a single profile,
 *  or a selection from the People list). Group-based segmentation is
 *  deliberately out of scope for v1 — tags already cover the common case,
 *  and reaching into Groups' own schema would add a second cross-service
 *  dependency for a filter shape that mostly overlaps what tagging already
 *  expresses. */
export type AudienceFilter =
  | { readonly kind: 'ALL' }
  | { readonly kind: 'TAGS'; readonly tagIds: readonly string[] }
  | { readonly kind: 'PERSON_IDS'; readonly personIds: readonly string[] };

// --- Custom fields ------------------------------------------------------------

/**
 * Administrator-defined extra fields on Person, gated behind the
 * `people.custom_fields` entitlement.
 *
 * Deliberately smaller than Forms' field-type set: EMAIL, PHONE and RADIO are
 * left out because Person already has structured contacts, and a sixth
 * choice-style type would be redundant with SELECT.
 */
export const PERSON_CUSTOM_FIELD_TYPES = [
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'DATE',
  'SELECT',
  'CHECKBOX',
] as const;
export type PersonCustomFieldType = (typeof PERSON_CUSTOM_FIELD_TYPES)[number];

const customFieldOptionsSchema = z.array(z.string().trim().min(1).max(200)).max(50).nullish();

/**
 * `key` is never supplied by the caller — it is derived from `label` at
 * creation time and returned in every response, but this endpoint only ever
 * takes a label, matching how an administrator actually thinks about it.
 */
export const createCustomFieldDefinitionRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(200),
    type: z.enum(PERSON_CUSTOM_FIELD_TYPES),
    required: z.boolean().default(false),
    options: customFieldOptionsSchema,
  })
  .refine((field) => field.type !== 'SELECT' || (field.options?.length ?? 0) > 0, {
    message: 'A SELECT field needs at least one option',
    path: ['options'],
  });
export type CreateCustomFieldDefinitionRequest = z.infer<
  typeof createCustomFieldDefinitionRequestSchema
>;

/**
 * `type` cannot be changed here — see `PersonCustomFieldDefinition` for why.
 * Declared field by field rather than `.partial()`, for the same reason
 * `updatePersonRequestSchema` above is: `.partial()` keeps `.default()`
 * values, so an empty PATCH would silently overwrite `required` with `false`.
 */
export const updateCustomFieldDefinitionRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(200).optional(),
    required: z.boolean().optional(),
    options: customFieldOptionsSchema,
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateCustomFieldDefinitionRequest = z.infer<
  typeof updateCustomFieldDefinitionRequestSchema
>;

export const listCustomFieldDefinitionsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListCustomFieldDefinitionsQuery = z.infer<typeof listCustomFieldDefinitionsQuerySchema>;

/** A person's custom field values, keyed by definition `key`. Validated
 *  server-side against the tenant's active definitions before being stored. */
export const setPersonCustomFieldsRequestSchema = z.object({
  values: z.record(z.string(), z.unknown()),
});
export type SetPersonCustomFieldsRequest = z.infer<typeof setPersonCustomFieldsRequestSchema>;

export interface CustomFieldDefinitionResponse {
  readonly id: string;
  readonly key: string;
  readonly label: string;
  readonly type: PersonCustomFieldType;
  readonly required: boolean;
  readonly options: readonly string[] | null;
  readonly archivedAt: string | null;
}

/** One definition merged with this person's stored value for it — `null`
 *  when nothing has been recorded yet. */
export interface PersonCustomFieldResponse extends CustomFieldDefinitionResponse {
  readonly value: unknown;
}

// --- Transfers ----------------------------------------------------------------

export const TRANSFER_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

export const createTransferRequestSchema = z.object({
  toCampusId: z.uuid(),
  reason: z.string().trim().min(1).max(500),
});
export type CreateTransferRequest = z.infer<typeof createTransferRequestSchema>;

export const decideTransferRequestSchema = z.object({
  note: z.string().trim().max(500).nullish(),
});
export type DecideTransferRequest = z.infer<typeof decideTransferRequestSchema>;

/** Campus names are not resolved here — the caller already has the
 *  tenant's campus list (the same one every other campus picker uses) to
 *  join against `fromCampusId`/`toCampusId` client-side, which avoids a new
 *  people-service → tenancy-service dependency for two id-to-name lookups. */
export interface TransferResponse {
  readonly id: string;
  readonly personId: string;
  readonly personDisplayName: string;
  readonly fromCampusId: string | null;
  readonly toCampusId: string;
  readonly reason: string;
  readonly status: TransferStatus;
  readonly requestedByUserId: string;
  readonly requestedAt: string;
  readonly decidedByUserId: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
}

// --- Followups ------------------------------------------------------------------

export const FOLLOWUP_TYPES = ['VISIT', 'CALL', 'MEETING', 'NOTE', 'SYSTEM_ALERT'] as const;
export type FollowupType = (typeof FOLLOWUP_TYPES)[number];
/** Staff can log any of these; `SYSTEM_ALERT` is reserved for the
 *  inactivity job — see `createFollowupRequestSchema`. */
export const STAFF_FOLLOWUP_TYPES = ['VISIT', 'CALL', 'MEETING', 'NOTE'] as const;

export const FOLLOWUP_STAGES = ['NEW', 'CONTACTED', 'IN_PROGRESS', 'INTEGRATED', 'CLOSED'] as const;
export type FollowupStage = (typeof FOLLOWUP_STAGES)[number];

export const createFollowupRequestSchema = z.object({
  type: z.enum(STAFF_FOLLOWUP_TYPES),
  stage: z.enum(FOLLOWUP_STAGES),
  note: z.string().trim().max(2000).nullish(),
  /** Defaults to now — a staff member logging a visit that happened
   *  yesterday can backdate it. */
  occurredAt: z.iso.datetime().nullish(),
  nextContactAt: z.iso.datetime().nullish(),
  assignedToUserId: z.uuid().nullish(),
});
export type CreateFollowupRequest = z.infer<typeof createFollowupRequestSchema>;

export interface FollowupResponse {
  readonly id: string;
  readonly personId: string;
  readonly type: FollowupType;
  readonly stage: FollowupStage;
  readonly note: string | null;
  readonly occurredAt: string;
  readonly nextContactAt: string | null;
  readonly assignedToUserId: string | null;
  readonly createdByUserId: string | null;
}

/** One person's place on the integration pipeline board — their latest
 *  followup entry, merged with their name for display. */
export interface FollowupPipelineEntryResponse {
  readonly personId: string;
  readonly personDisplayName: string;
  readonly stage: FollowupStage;
  readonly latestFollowup: FollowupResponse;
}

// --- Growth path ----------------------------------------------------------------

const growthStepNameField = z.string().trim().min(1).max(150);

export const createGrowthStepRequestSchema = z.object({
  name: growthStepNameField,
  order: z.number().int().default(0),
});
export type CreateGrowthStepRequest = z.infer<typeof createGrowthStepRequestSchema>;

export const updateGrowthStepRequestSchema = z
  .object({
    name: growthStepNameField.optional(),
    order: z.number().int().optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateGrowthStepRequest = z.infer<typeof updateGrowthStepRequestSchema>;

export const listGrowthStepsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListGrowthStepsQuery = z.infer<typeof listGrowthStepsQuerySchema>;

export const setPersonGrowthStepRequestSchema = z.object({
  /** Null clears the person's progress — back to "not started". */
  stepId: z.uuid().nullish(),
});
export type SetPersonGrowthStepRequest = z.infer<typeof setPersonGrowthStepRequestSchema>;

export interface GrowthStepResponse {
  readonly id: string;
  readonly name: string;
  readonly order: number;
  readonly archivedAt: string | null;
}

export interface PersonGrowthProgressResponse {
  readonly personId: string;
  readonly stepId: string | null;
  readonly stepName: string | null;
  readonly reachedAt: string | null;
}

/** How many people currently sit at each step — the shape of the whole
 *  congregation's discipleship progress, not a roster. */
export interface GrowthReportEntryResponse {
  readonly step: GrowthStepResponse;
  readonly personCount: number;
}
